<script setup>
// =============================================================================
// FangateUploadModal — new-content upload flow
//
// Two-column modal: drop zone + form on the left, price/payout/compliance
// on the right. CTA at the bottom of the right column.
//
// State: when connected, confirm runs the REAL upload pipeline via FangateAPI
// (chunked /api/upload-sessions → complete → POST /products → PATCH flags). In
// demo mode (no token) it simulates with a short delay and emits a synthesised
// product so the panel still renders. The `uploaded` event contract is the
// same in both paths, so the rest of the panel doesn't change.
//
// Self-contained: owns its file input, form state, validation, payout
// calc, and the (currently simulated) save flow.
// =============================================================================

import { ref, reactive, computed, watch } from 'vue';
import FangateAPI from 'dashboard/api/fangate';
import { useFangateCreatorNetwork } from '../composables/useFangateCreatorNetwork';

const props = defineProps({
  open:           { type: Boolean, default: false },
  // Display name for the "to vault {X}" subtitle. Parent resolves the
  // fallback chain (creator.name.first → inbox.name → 'Creator').
  vaultName:      { type: String,  default: 'Creator' },
  // When false → simulate (no real API). When true → run the real
  // upload-sessions → POST /products → PATCH flags flow against Fangate.
  isConnected:    { type: Boolean, default: false },
  // Locale-aware money formatter.
  formatBalance:  { type: Function, default: n => String(n) },
  // Standalone currency symbol for the price prefix.
  currencySymbol: { type: String,  default: '€' },
});

const emit = defineEmits([
  'update:open',
  // payload: a freshly-built product { id, title, price, type, items, thumb, folder }
  // — when isConnected, this is Fangate's actual response (normalized);
  // in demo mode, a synthesised stub so the UI is still testable.
  'uploaded',
]);

const state = reactive({
  type:          'photo',      // 'photo' | 'video'
  files:         [],
  title:         '',
  publicDesc:    '',
  internalNotes: '',
  price:         '0.00',
  flags: {
    collection: false,
    avs18:      true,
    consent:    false,
    download:   true,
    agecheck:   false,
  },
  saving: false,
  error:  '',
  // Progress text shown next to the CTA spinner — e.g. "Uploading 12/45 MB".
  progress: '',
});

// ---- Co-creator tagging (collected here, fired after POST /products) ----
// During upload the product doesn't exist yet, so we can't tag against an
// id. Instead we collect the intended tags and run them through
// addProductConsentTag once the product id is returned by createProduct.
const coTagSearch = reactive({
  query:    '',
  results:  [],
  loading:  false,
  error:    '',
});
// Selected tags-to-fire after product creation. Each item is either
//   { creatorId, label }   (picked from search results), or
//   { email, label }       (typed-in email for the invite-mode fallback).
const coTagsToAdd = ref([]);
let coTagsSearchTimer = null;
const onCoTagInput = () => {
  if (coTagsSearchTimer) clearTimeout(coTagsSearchTimer);
  coTagsSearchTimer = setTimeout(runCoTagSearch, 220);
};
const runCoTagSearch = async () => {
  const q = String(coTagSearch.query || '').trim();
  if (q.length < 2) { coTagSearch.results = []; coTagSearch.error = ''; return; }
  coTagSearch.loading = true; coTagSearch.error = '';
  try {
    const r = await FangateAPI.searchCreators(q);
    coTagSearch.results = Array.isArray(r) ? r : [];
  } catch (e) {
    coTagSearch.results = [];
    coTagSearch.error = e?.message || 'Search failed';
  } finally {
    coTagSearch.loading = false;
  }
};
const creatorLabelOf = c => {
  if (!c) return 'Unknown';
  if (c.display_name) return c.display_name;
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || `Creator #${c.id ?? '?'}`;
};
const isEmailLike = s => typeof s === 'string' && /^\S+@\S+\.\S+$/.test(s.trim());
// Email-invite hidden (audit #5): Fangate accepts the consent POST but does NOT
// actually send the invite email — a silent no-op. Mirrors the Vault (#97).
// Tag-by-Fangate-creator still works. Re-enable once Fangate confirms delivery.
const showEmailInvite = computed(() => false);
const addCoTagFromCreator = c => {
  if (!c?.id) return;
  if (coTagsToAdd.value.some(t => t.creatorId === c.id)) return;
  coTagsToAdd.value = [...coTagsToAdd.value, {
    creatorId: c.id,
    label:     creatorLabelOf(c),
  }];
  coTagSearch.query   = '';
  coTagSearch.results = [];
};
const addCoTagFromEmail = () => {
  const email = String(coTagSearch.query || '').trim();
  if (!isEmailLike(email)) return;
  if (coTagsToAdd.value.some(t => t.email && t.email.toLowerCase() === email.toLowerCase())) return;
  coTagsToAdd.value = [...coTagsToAdd.value, { email, label: email }];
  coTagSearch.query   = '';
  coTagSearch.results = [];
};
const removeCoTag = idx => {
  coTagsToAdd.value = coTagsToAdd.value.filter((_, i) => i !== idx);
};

// Quick-select: co-creators who previously accepted a tag (re-tag without a new
// approval). Loaded lazily/cached when the consent flag turns on.
const { networkState: coNetworkState, loadNetwork: loadCoNetwork } = useFangateCreatorNetwork();
watch(() => state.flags.consent, on => { if (on) loadCoNetwork(); });
const coTagQuickPicks = computed(() => {
  const taggedIds = new Set(coTagsToAdd.value.map(t => t.creatorId));
  return (coNetworkState().creators || []).filter(c => !taggedIds.has(c.id)).slice(0, 12);
});

const fileInput = ref(null);
const cameraInput = ref(null);   // dedicated camera-capture input (mobile "Take photo")

// Reset to a fresh state whenever the modal opens. Closing leaves stale
// values around (cheap); the next open clears them. Matches the price modal.
watch(() => props.open, isOpen => {
  if (!isOpen) return;
  revokeAll();  // drop any stale blob URLs from a previous open
  state.type          = 'photo';
  state.files         = [];
  state.title         = '';
  state.publicDesc    = '';
  state.internalNotes = '';
  state.price         = '0.00';
  state.flags         = { collection: false, avs18: true, consent: false, download: true, agecheck: false };
  state.error         = '';
  state.progress      = '';
  coTagSearch.query   = '';
  coTagSearch.results = [];
  coTagSearch.error   = '';
  coTagsToAdd.value   = [];
});

const close = () => {
  emit('update:open', false);
  // Free all blob URLs for previews — left to leak otherwise (page-lifetime).
  revokeAll();
  if (typeof document !== 'undefined') document.activeElement?.blur?.();
};

const setType = t => {
  state.type = t;
  if (fileInput.value) {
    fileInput.value.accept = t === 'photo' ? 'image/*' : 'video/*';
    fileInput.value.click();
  }
};
const triggerPicker = () => fileInput.value?.click();
// Mobile "Take photo": a dedicated input with capture="environment" opens the
// device camera directly. addFiles() infers state.type from the returned image,
// so no manual type juggling. Only surfaced on touch devices (capture is a no-op
// on desktop → would just reopen the file picker).
const isTouchDevice = typeof window !== 'undefined'
  && !!window.matchMedia?.('(pointer: coarse)')?.matches;
const takePhoto = () => cameraInput.value?.click();

// ----- File preview URL management -----------------------------------------
// Generate object URLs lazily and cache them so previewFor() returns the
// same URL for the same File object across re-renders. URLs are revoked on
// file remove and on modal close to avoid leaking blob memory.
const previewUrls = new Map();
const previewFor = file => {
  if (!file) return null;
  if (previewUrls.has(file)) return previewUrls.get(file);
  if ((file.type || '').startsWith('image/')) {
    const url = URL.createObjectURL(file);
    previewUrls.set(file, url);
    return url;
  }
  // Video / other → no preview, will show an icon tile.
  previewUrls.set(file, null);
  return null;
};
const revokeAll = () => {
  previewUrls.forEach(url => { if (url) URL.revokeObjectURL(url); });
  previewUrls.clear();
};

// ----- File list mutators (additive — picker + drop both call addFiles) ----
// addFiles appends instead of replacing so the user can pick → pick again →
// drag → still have one growing list (the old behaviour replaced everything
// on each picker invocation).
const addFiles = list => {
  const incoming = Array.from(list || []);
  if (!incoming.length) return;
  // Sync the type to what was ACTUALLY picked. iOS pickers (esp. "Choose File" /
  // the Files app) ignore the `accept` hint and can hand back an image while the
  // type toggle still says "video" (or vice-versa) → validation then rejected it
  // against the wrong allow-list ("unsupported type image/jpeg. Allowed: video/…").
  // Infer from the first file's MIME so a photo validates as a photo.
  const firstMime = incoming[0]?.type || '';
  if (firstMime.startsWith('image/')) state.type = 'photo';
  else if (firstMime.startsWith('video/')) state.type = 'video';
  state.files = [...state.files, ...incoming];
};
const onFileChange = e => {
  addFiles(e.target.files);
  e.target.value = '';   // reset so picking the same file again re-fires change
};
const removeFile = idx => {
  const file = state.files[idx];
  if (file && previewUrls.has(file)) {
    const url = previewUrls.get(file);
    if (url) URL.revokeObjectURL(url);
    previewUrls.delete(file);
  }
  state.files = state.files.filter((_, i) => i !== idx);
};

// ----- Drag-and-drop -------------------------------------------------------
// Visual feedback ref — dropzone gets a "dragover" class when a drag enters
// to make it obvious the drop is accepted.
const isDragOver = ref(false);
const onDragOver = e => {
  e.preventDefault();
  // dragenter and dragover both fire — we just need ONE state flip.
  if (!isDragOver.value) isDragOver.value = true;
};
const onDragLeave = e => {
  // dragleave fires for child elements too — only flip off when leaving the
  // dropzone itself (currentTarget === dropzone, target is whatever leaves).
  if (e.currentTarget === e.target) isDragOver.value = false;
};
const onDrop = e => {
  e.preventDefault();
  isDragOver.value = false;
  addFiles(e.dataTransfer?.files);
};

const toggleFlag = key => {
  state.flags[key] = !state.flags[key];
  // Linked: agecheck only meaningful with AVS on. Forcibly clear on AVS off.
  if (key === 'avs18' && !state.flags.avs18) state.flags.agecheck = false;
};

// Payout estimate: 87.5% of gross minus a flat 0.50 fee (Fangate's structure).
// The 0.50 is currency-symbol-formatted via formatBalance — for non-EUR
// accounts the math still treats the fee as 0.50 in the active currency,
// which matches what the docs claim (no per-currency exchange).
const payoutEstimate = computed(() => {
  const price = parseFloat(state.price) || 0;
  if (price <= 0) return null;
  const value = price * 0.875 - 0.50;
  return value > 0 ? value : 0;
});

// ===========================================================================
// Validation helpers
// ===========================================================================
// Match Fangate's documented allow-lists so the user gets a clear client-side
// error instead of an init-failure deep in the upload flow.
const allowedMimesForType = t => t === 'photo'
  ? FangateAPI.UPLOAD_LIMITS.ALLOWED_IMAGE_MIMES
  : FangateAPI.UPLOAD_LIMITS.ALLOWED_VIDEO_MIMES;

const validateFiles = () => {
  const allowed = allowedMimesForType(state.type);
  for (const f of state.files) {
    if (f.size > FangateAPI.UPLOAD_LIMITS.MAX_FILE_BYTES) {
      return `${f.name} exceeds the 5 GB limit.`;
    }
    if (!allowed.includes(f.type)) {
      return `${f.name} has unsupported type "${f.type || 'unknown'}".\n` +
             `Allowed: ${allowed.join(', ')}.`;
    }
  }
  return '';
};

// ===========================================================================
// Single-file upload — runs the full Fangate flow for ONE file.
// Branches on session.upload_strategy:
//   • single_part → one PUT to session.initial_upload, no /parts calls
//   • multipart   → loop /parts → S3 PUT per chunk, then /complete with parts list
// Returns { sessionId } so the caller can wire the first session into
// POST /products and subsequent sessions into the resulting product.
// ===========================================================================
const uploadOneFile = async (file, { productId = null } = {}) => {
  const assetKind = state.type === 'photo' ? 'image' : 'video';

  // 1. INIT
  state.progress = `Preparing ${file.name}…`;
  const session = await FangateAPI.createUploadSession({
    fileName:  file.name,
    fileSize:  file.size,
    mimeType:  file.type,
    assetKind,
    productId,
  });
  if (!session?.id) throw new Error('Upload init returned no session id.');

  // Fangate picks the strategy server-side based on file size + asset kind.
  // Trust the response; fall back to the documented threshold if the field
  // is missing (older API versions).
  const strategy = session.upload_strategy || (
    file.size <= FangateAPI.UPLOAD_LIMITS.MULTIPART_THRESHOLD
      ? 'single_part'
      : 'multipart'
  );

  if (strategy === 'single_part') {
    // 2a. SINGLE PUT — the init response carries an initial_upload presigned URL.
    //     Calling POST /parts on a single_part session returns HTTP 422
    //     "This upload session does not use multipart uploads."
    const initial = session.initial_upload;
    if (!initial?.url) {
      throw new Error('Single-part upload session is missing the initial_upload URL.');
    }
    state.progress = `Uploading ${file.name}…`;
    await FangateAPI.putChunkToS3(initial, file);

    // 3a. COMPLETE without a parts list (server tracked the single PUT).
    state.progress = `Finalizing ${file.name}…`;
    await FangateAPI.completeUploadSession(session.id, null);
  } else {
    // 2b. MULTIPART — request a presigned URL per chunk and PUT each to S3.
    const chunkSize  = FangateAPI.UPLOAD_LIMITS.MIN_PART_BYTES;     // 8 MB
    const totalParts = Math.max(1, Math.ceil(file.size / chunkSize));
    const completedParts = [];

    for (let i = 0; i < totalParts; i++) {
      const start = i * chunkSize;
      const end   = Math.min(start + chunkSize, file.size);
      const blob  = file.slice(start, end);

      state.progress = `Uploading ${file.name}: part ${i + 1} / ${totalParts}`;
      const part = await FangateAPI.getUploadPart(session.id, i + 1);
      const etag = await FangateAPI.putChunkToS3(part, blob);
      completedParts.push({ part_number: i + 1, etag, size_bytes: end - start });
    }

    // 3b. COMPLETE with the etag list.
    state.progress = `Finalizing ${file.name}…`;
    await FangateAPI.completeUploadSession(session.id, completedParts);
  }

  // 4. POLL until ready (or terminal failure).
  state.progress = `Processing ${file.name}…`;
  await FangateAPI.waitForUploadReady(session.id, {
    onTick: status => { state.progress = `Processing ${file.name} (${status})…`; },
  });

  return { sessionId: session.id };
};

// ===========================================================================
// Confirm — the user clicked "Create link". Validate, then either simulate
// (demo) or drive the real Fangate upload flow.
// ===========================================================================
const confirm = async () => {
  if (!state.title.trim())  { state.error = 'Title is required.';                return; }
  if (!state.files.length)  { state.error = 'Please select at least one file.'; return; }

  const priceEur = parseFloat(state.price) || 0;
  if (priceEur < 5 || priceEur > 224) {
    state.error = 'Price must be between € 5 and € 224.';
    return;
  }

  state.saving   = true;
  state.error    = '';
  state.progress = '';

  // ----- Demo mode (no live token) -----
  if (!props.isConnected) {
    try {
      await new Promise(r => setTimeout(r, 500));
      const newId = 'u' + Date.now();
      emit('uploaded', {
        id:     newId,
        title:  state.title,
        price:  priceEur,
        // Match Fangate's API convention so the toolbar's type filter
        // matches this product the same way it matches a real /products GET.
        type:   state.type === 'photo' ? 'image' : 'video',
        items:  state.type === 'photo' ? state.files.length : null,
        thumb:  '',
        folder: null,
      });
      close();
    } catch (e) {
      state.error = e?.message || 'Upload failed.';
    } finally {
      state.saving = false; state.progress = '';
    }
    return;
  }

  // ----- Live mode -----
  const mimeError = validateFiles();
  if (mimeError) {
    state.error = mimeError;
    state.saving = false;
    return;
  }

  const priceCents = Math.round(priceEur * 100);
  const sessionIds = [];   // for cleanup on partial failure
  let productId = null;

  try {
    // Wrap each API call with a `[step-label]` prefix so a Fangate error
    // surfaces WHICH step failed (otherwise everything looks like "HTTP 403"
    // and the user has to dig through DevTools to find the culprit URL).
    const step = async (label, fn) => {
      try { return await fn(); }
      catch (e) { throw new Error(`[${label}] ${e?.message || String(e)}`); }
    };

    let createdProduct = null;

    // Upload each file. First one creates the product; rest attach to it.
    for (let i = 0; i < state.files.length; i++) {
      const file = state.files[i];
      const { sessionId } = await step(`upload file ${i + 1}/${state.files.length}`,
        () => uploadOneFile(file, { productId }));
      sessionIds.push(sessionId);

      // After the FIRST file's session is ready, create the product. The
      // product is bound to that session; subsequent sessions will attach
      // their media to the same product via `productId`.
      if (productId == null) {
        state.progress = 'Creating product…';
        createdProduct = await step('POST /products', () =>
          FangateAPI.createProduct({
            uploadSessionId:    sessionId,
            price:              priceCents,
            title:              state.title,
            publicDescription:  state.publicDesc  || undefined,
            privateDescription: state.internalNotes || undefined,
            isAdultContent:     state.flags.avs18,
            isVerifAge:         state.flags.agecheck,
            isShouldConsent:    state.flags.consent,
          }));
        productId = createdProduct?.id;
        if (!productId) throw new Error('[POST /products] returned no id.');
      }
    }

    // Side-channel flags that POST /products doesn't accept:
    //   is_downloadable    → PATCH /products/{id}
    //   in_collection true → POST  /products/{id}/collection (toggle)
    // We always send is_downloadable so a non-default value lands; the
    // collection toggle only fires if the user wants the product in it.
    state.progress = 'Applying access flags…';
    await step('PATCH /products/{id}', () =>
      FangateAPI.updateProduct(productId, { is_downloadable: state.flags.download }));
    if (state.flags.collection) {
      await step('POST /products/{id}/collection', () =>
        FangateAPI.toggleProductCollection(productId));
    }

    // Fire collected co-creator consent tags now that we have a real
    // productId. Each call is independent — a per-tag failure doesn't
    // abort the upload (product is already created); errors get collected
    // and surfaced as a non-fatal summary on the modal.
    if (state.flags.consent && coTagsToAdd.value.length) {
      const tagErrors = [];
      const today = new Date().toISOString().slice(0, 10);
      for (let i = 0; i < coTagsToAdd.value.length; i++) {
        const t = coTagsToAdd.value[i];
        state.progress = `Tagging co-creator ${i + 1}/${coTagsToAdd.value.length}…`;
        try {
          await FangateAPI.addProductConsentTag({
            productId,
            date:            today,
            taggedCreatorId: t.creatorId || undefined,
            email:           t.creatorId ? undefined : t.email,
          });
        } catch (e) {
          tagErrors.push(`${t.label}: ${e?.message || 'failed'}`);
        }
      }
      if (tagErrors.length) {
        // Surface as soft error; product still created, modal still closes.
        // eslint-disable-next-line no-console
        console.warn('[Fangate] Some co-creator tags failed:', tagErrors);
        state.error = 'Product created, but some co-creator tags failed: ' + tagErrors.join('; ');
      }
    }

    // Do NOT refetch via GET /products/{id} — Fangate returns 403 for
    // freshly created products until SightEngine moderation finishes
    // (images: seconds; videos: manual review). The POST /products response
    // already gives us the full product (with `link` and the cents price);
    // we round-trip those plus our own input state into a "good enough"
    // product the panel can render immediately. The next manual refresh
    // (toolbar refresh button) will pick up the moderated version with
    // full media[].
    emit('uploaded', {
      // Best-effort merge: createProduct response wins on server-authoritative
      // fields (id, link, server-confirmed price); our local state fills in
      // the rest so the card is renderable without media[].
      id:     createdProduct?.id || productId,
      title:  createdProduct?.title || state.title,
      price:  typeof createdProduct?.price === 'number'
                ? createdProduct.price / 100
                : priceEur,
      link:   createdProduct?.link || undefined,
      // Match Fangate's API convention (image/video, lowercase) so filters
      // and grouping treat the new product like any /products GET result.
      type:   state.type === 'photo' ? 'image' : 'video',
      items:  state.type === 'photo' ? state.files.length : null,
      thumb:  '',
      folder: null,
      // Carry through the flags too so the panel can show them if needed.
      is_adult_content:  state.flags.avs18,
      is_verif_age:      state.flags.agecheck,
      is_downloadable:   state.flags.download,
      in_collection:     state.flags.collection,
      is_should_consent: state.flags.consent,
    });
    close();
  } catch (e) {
    // Best-effort cleanup of any in-flight sessions so storage isn't
    // littered with stranded uploads. The product, if created, stays.
    sessionIds.forEach(sid => { FangateAPI.abortUploadSession(sid); });
    state.error = e?.message || 'Upload failed.';
  } finally {
    state.saving   = false;
    state.progress = '';
  }
};
</script>

<template>
  <!-- Teleport to <body> so the fixed overlay anchors to the viewport, not to
       the Fangate panel (whose transform/overflow was clipping the modal's top).
       NOTE: no @click.self="close" on the overlay — the modal can only be
       dismissed via the explicit "Close" button in the header. This protects
       a half-filled upload from being lost to a stray miss-click on the
       backdrop. -->
  <Teleport to="body">
  <div v-if="open" class="fp-modal-overlay fp-modal-overlay--upload">
    <div class="fp-up-modal" @click.stop>
      <!-- Header -->
      <div class="fp-up-head">
        <div class="fp-up-head-inner">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient id="fp-up-grad" x1="0" y1="0" x2="24" y2="24">
                <stop offset="0%"   stop-color="#1a0a6b"/>
                <stop offset="100%" stop-color="#7040e0"/>
              </linearGradient>
            </defs>
            <path d="M13 2 L4 14 L11 14 L11 22 L20 10 L13 10 Z" fill="url(#fp-up-grad)"/>
          </svg>
          <div>
            <div class="fp-up-title">New Upload</div>
            <div class="fp-up-sub">
              to vault <strong>{{ vaultName }}</strong>
            </div>
          </div>
        </div>
        <button class="fp-up-close" @click="close" title="Close upload">
          Close
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <!-- Body -->
      <div class="fp-up-body">
        <input ref="fileInput" type="file" multiple
               :accept="state.type === 'photo' ? 'image/*' : 'video/*'"
               style="display:none;" @change="onFileChange" />
        <input ref="cameraInput" type="file" accept="image/*" capture="environment"
               style="display:none;" @change="onFileChange" />

        <!-- LEFT column -->
        <div class="fp-up-col">
          <!-- Drop zone — also handles drag-and-drop (dragover/drop). Visual
               feedback via the .is-dragover class while a drag is over it. -->
          <div class="fp-up-dropzone"
               :class="{ 'is-dragover': isDragOver }"
               @click="triggerPicker"
               @dragover="onDragOver"
               @dragenter="onDragOver"
               @dragleave="onDragLeave"
               @drop="onDrop">
            <div class="fp-up-dropicon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="#7040e0" stroke-width="1.8"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <div class="fp-up-droptitle">
              {{ isDragOver ? 'Drop to add' : 'Drag media here or choose a type' }}
            </div>
            <div v-if="state.files.length" class="fp-up-filesnote">
              {{ state.files.length }} file{{ state.files.length === 1 ? '' : 's' }} selected
            </div>
            <div class="fp-up-typebar">
              <button class="fp-up-typebtn"
                      :class="{ selected: state.type === 'photo' }"
                      @click.stop="setType('photo')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                Add photos
              </button>
              <button class="fp-up-typebtn"
                      :class="{ selected: state.type === 'video' }"
                      @click.stop="setType('video')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2">
                  <rect x="2" y="6" width="14" height="12" rx="2"/>
                  <polygon points="22 8 16 12 22 16 22 8"/>
                </svg>
                Add video
              </button>
              <button v-if="isTouchDevice" class="fp-up-typebtn" @click.stop="takePhoto">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Take photo
              </button>
            </div>

            <!-- File thumbnail tiles. Image files render a preview via
                 URL.createObjectURL; videos show a generic video icon
                 (browsers can't cheaply generate frame thumbnails without
                 decoding). Each tile has an × button to drop it from the
                 upload. A trailing "+" tile opens the picker for more. -->
            <div v-if="state.files.length" class="fp-up-thumbgrid">
              <div v-for="(f, idx) in state.files" :key="idx"
                   class="fp-up-thumb"
                   :title="f.name">
                <img v-if="previewFor(f)" :src="previewFor(f)" :alt="f.name" />
                <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="#7040e0" stroke-width="1.8"
                     stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="6" width="14" height="12" rx="2"/>
                  <polygon points="22 8 16 12 22 16 22 8"/>
                </svg>
                <span class="fp-up-thumb-name">{{ f.name }}</span>
                <button class="fp-up-thumb-x"
                        @click.stop="removeFile(idx)"
                        :title="'Remove ' + f.name">×</button>
              </div>
              <!-- + tile: explicit add-more affordance. -->
              <button class="fp-up-thumb fp-up-thumb--add"
                      @click.stop="triggerPicker"
                      title="Add more files">
                <span class="fp-up-thumb-plus">+</span>
                <span class="fp-up-thumb-addlabel">Add more</span>
              </button>
            </div>
          </div>

          <!-- Title + Public description -->
          <div class="fp-up-card">
            <div class="fp-up-label">Title</div>
            <input v-model="state.title" maxlength="50"
                   placeholder="Add a short, descriptive title"
                   class="fp-up-input" />
            <div class="fp-up-counter">{{ state.title.length }}/50</div>

            <div class="fp-up-label" style="margin-top:10px;">Public description</div>
            <textarea v-model="state.publicDesc" maxlength="500" rows="3"
                      placeholder="Visible to buyers — describe what they'll unlock."
                      class="fp-up-textarea"/>
            <div class="fp-up-counter">{{ state.publicDesc.length }}/500</div>
          </div>

          <!-- Internal notes -->
          <div class="fp-up-card">
            <div class="fp-up-note-explain">
              Private notes stay inside Fangate and help you keep track of the release.
            </div>
            <div class="fp-up-label">Internal notes</div>
            <textarea v-model="state.internalNotes" maxlength="500" rows="3"
                      placeholder="Only you can see this — notes, reminders, shoot details."
                      class="fp-up-textarea"/>
            <div class="fp-up-counter">{{ state.internalNotes.length }}/500</div>
          </div>
        </div>

        <!-- RIGHT column -->
        <div class="fp-up-col">
          <div class="fp-up-card">
            <div class="fp-up-label">Price</div>
            <div class="fp-up-pricefield">
              <span>{{ currencySymbol }}</span>
              <input v-model="state.price" type="number" min="5" max="224" step="0.01" />
            </div>
            <div class="fp-up-pricehint">
              Min price: {{ formatBalance(5) }} — Max price: {{ formatBalance(224) }}
            </div>
          </div>

          <div class="fp-up-card fp-up-payoutcard">
            <span>Estimated payout</span>
            <span v-if="payoutEstimate == null" class="fp-up-payout-na">Unavailable</span>
            <span v-else class="fp-up-payout-val">{{ formatBalance(payoutEstimate) }}</span>
          </div>

          <div class="fp-up-card">
            <div class="fp-up-label" style="margin-bottom:8px;">Access &amp; Compliance</div>

            <div class="fp-up-flag">
              <div>
                <div class="fp-up-flag-title">Add to Collection</div>
                <div class="fp-up-flag-desc">Include this item in your collection page once upload completes.</div>
              </div>
              <button class="fp-switch" :class="{ on: state.flags.collection }"
                      @click="toggleFlag('collection')">
                <span class="fp-switch-knob"></span>
              </button>
            </div>

            <div class="fp-up-flag">
              <div>
                <div class="fp-up-flag-title">AVS (18+)</div>
                <div class="fp-up-flag-desc">Mark this content as adult and apply adult-content rules.</div>
              </div>
              <button class="fp-switch" :class="{ on: state.flags.avs18 }"
                      @click="toggleFlag('avs18')">
                <span class="fp-switch-knob"></span>
              </button>
            </div>

            <div class="fp-up-flag">
              <div>
                <div class="fp-up-flag-title">Third-party consent required</div>
                <div class="fp-up-flag-desc">
                  If a third person is visible in this content, consent must be
                  obtained by tagging them — provide their name and email below.
                  Fangate will email this address with the consent request.
                </div>
              </div>
              <button class="fp-switch" :class="{ on: state.flags.consent }"
                      @click="toggleFlag('consent')">
                <span class="fp-switch-knob"></span>
              </button>
            </div>

            <!-- Co-creator tagging (collected here, fired after the product
                 is created on Fangate). Shown only when "Third-party consent
                 required" is on. -->
            <div v-if="state.flags.consent" class="fp-up-consent-block">
              <div class="fp-up-consent-title">Tag co-creators</div>
              <div class="fp-up-consent-body">
                If another person is visible in this content, tag them now —
                Fangate will send each tagged creator a consent request after
                the upload completes. Use a Fangate name/email; if they don't
                have an account, Fangate sends them an email invite.
              </div>

              <!-- Selected tags so far -->
              <div v-if="coTagsToAdd.length" class="fp-up-cotag-chips">
                <div v-for="(t, idx) in coTagsToAdd" :key="idx" class="fp-up-cotag-chip">
                  <span>{{ t.label }}</span>
                  <button class="fp-up-cotag-x" @click.stop="removeCoTag(idx)">×</button>
                </div>
              </div>

              <!-- Quick-select: previously-accepted co-creators (re-tag, no new approval) -->
              <div v-if="coNetworkState().loading" class="fp-up-cotag-hint">Loading your collaborators…</div>
              <div v-else-if="coTagQuickPicks.length" class="fp-up-cotag-quick">
                <span class="fp-up-cotag-quick-label">Previously accepted — tap to tag again:</span>
                <div class="fp-up-cotag-chips">
                  <button v-for="c in coTagQuickPicks" :key="'qp-' + c.id" type="button"
                          class="fp-up-cotag-chip fp-up-cotag-chip--pick"
                          @click.stop="addCoTagFromCreator(c)">
                    + {{ creatorLabelOf(c) }}
                  </button>
                </div>
              </div>

              <!-- Search input — same UX as Vault detail -->
              <input v-model="coTagSearch.query" @input="onCoTagInput"
                     placeholder="Search creators by name or email…"
                     class="fp-up-cotag-input" />

              <div v-if="coTagSearch.loading" class="fp-up-cotag-hint">Searching…</div>
              <div v-else-if="coTagSearch.error" class="fp-up-cotag-err">
                {{ coTagSearch.error }}
              </div>
              <div v-else-if="coTagSearch.results.length" class="fp-up-cotag-results">
                <div v-for="c in coTagSearch.results" :key="c.id" class="fp-up-cotag-result">
                  <div class="fp-up-cotag-result-meta">
                    <div class="fp-up-cotag-result-name">{{ creatorLabelOf(c) }}</div>
                    <div v-if="c.email" class="fp-up-cotag-result-email">{{ c.email }}</div>
                  </div>
                  <button class="fp-up-cotag-addbtn" @click.stop="addCoTagFromCreator(c)">
                    Tag
                  </button>
                </div>
              </div>
              <div v-else-if="coTagSearch.query.trim().length >= 2" class="fp-up-cotag-empty">
                <div class="fp-up-cotag-hint">
                  No matches on Fangate for "{{ coTagSearch.query.trim() }}".
                </div>
                <button v-if="showEmailInvite"
                        class="fp-up-cotag-invitebtn"
                        @click.stop="addCoTagFromEmail">
                  📧 Send consent request to {{ coTagSearch.query.trim() }}
                </button>
              </div>
            </div>

            <div class="fp-up-flag">
              <div>
                <div class="fp-up-flag-title">Allow download</div>
                <div class="fp-up-flag-desc">Let buyers download purchased files after checkout.</div>
              </div>
              <button class="fp-switch" :class="{ on: state.flags.download }"
                      @click="toggleFlag('download')">
                <span class="fp-switch-knob"></span>
              </button>
            </div>

            <div class="fp-up-flag" :class="{ 'is-disabled': !state.flags.avs18 }">
              <div>
                <div class="fp-up-flag-title">Require age check</div>
                <div class="fp-up-flag-desc">Only available when AVS is enabled for this upload.</div>
              </div>
              <button class="fp-switch"
                      :class="{ on: state.flags.agecheck, disabled: !state.flags.avs18 }"
                      :disabled="!state.flags.avs18"
                      @click="toggleFlag('agecheck')">
                <span class="fp-switch-knob"></span>
              </button>
            </div>
          </div>

          <div v-if="state.error" class="fp-modal-err">{{ state.error }}</div>

          <button class="fp-up-cta"
                  :disabled="state.saving"
                  @click="confirm">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            <template v-if="state.saving">
              {{ state.progress || 'Uploading…' }}
            </template>
            <template v-else>Create link</template>
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<style lang="scss" scoped>
// Overlay — modal-specific (NOT shared with edit-price modal since that one
// has its own scoped overlay). The `--upload` variant adds a blur backdrop.
.fp-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(11, 20, 20, 0.35);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
  &--upload { background: rgba(20, 10, 60, 0.45); backdrop-filter: blur(4px); }
}
// Error chip — shared visual with the edit-price modal but each component
// keeps its own scoped copy now.
.fp-modal-err {
  padding: 8px 12px; border-radius: 8px;
  background: #fee2e2; color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}

// ===== Upload modal shell =====
.fp-up-modal {
  background: linear-gradient(180deg, #faf8ff 0%, #fff 100%);
  border-radius: 16px;
  width: 100%; max-width: 880px;
  max-height: 92vh;
  box-shadow: 0 24px 60px rgba(20, 10, 60, 0.30);
  display: flex; flex-direction: column; overflow: hidden;
}
.fp-up-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid rgba(91, 46, 207, 0.12);
  background: #fff;
  flex-shrink: 0;
}
.fp-up-head-inner { display: flex; align-items: center; gap: 10px; }
.fp-up-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px; font-weight: 800;
  color: #1a0a6b; letter-spacing: -0.01em;
}
.fp-up-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #7c6fcd; margin-top: 1px;
  strong { color: #5b2ecf; }
}
// Prominent close button — pill-shaped with "Close ×" label so it's
// obvious how to dismiss the modal (overlay clicks no longer close it).
.fp-up-close {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid rgba(91, 46, 207, 0.3);
  background: #fff;
  color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s, transform 0.1s;
  > span {
    display: inline-flex; align-items: center; justify-content: center;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(91, 46, 207, 0.12);
    font-size: 14px; line-height: 1;
    transition: background 0.12s, color 0.12s;
  }
  &:hover {
    background: linear-gradient(135deg, #5b2ecf, #7040e0);
    border-color: transparent;
    color: #fff;
    > span { background: rgba(255, 255, 255, 0.25); color: #fff; }
  }
  &:active { transform: scale(0.97); }
  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid #7040e0;
    outline-offset: 2px;
  }
}
.fp-up-body {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 16px; padding: 18px 22px 22px;
  flex: 1; overflow-y: auto;
}
.fp-up-col { display: flex; flex-direction: column; gap: 14px; }
// Mobile (≤768px): side-by-side columns get cramped + the right one (price /
// access & compliance) is cut off → stack everything in a single column.
@media (max-width: 768px) {
  .fp-up-body { grid-template-columns: 1fr; }
}
.fp-up-card {
  background: #fff;
  border: 1px solid rgba(91, 46, 207, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
}
.fp-up-label {
  display: flex; align-items: center; gap: 5px;
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #5b2ecf; margin-bottom: 7px;
}
.fp-up-input {
  width: 100%; box-sizing: border-box;
  padding: 9px 12px; border-radius: 9px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b;
  outline: none;
  &:focus { border-color: #7040e0; box-shadow: 0 0 0 3px rgba(112, 64, 224, 0.12); }
}
.fp-up-textarea {
  @extend .fp-up-input;
  resize: vertical; min-height: 70px; line-height: 1.45;
}
.fp-up-counter {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: #9b90e0;
  text-align: right; margin-top: 3px;
}
.fp-up-note-explain {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd;
  margin-bottom: 8px; line-height: 1.4;
}

// ===== Drop zone =====
.fp-up-dropzone {
  border: 2px dashed rgba(112, 64, 224, 0.35);
  border-radius: 13px;
  padding: 22px 18px;
  background: linear-gradient(180deg, rgba(112, 64, 224, 0.04), transparent);
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, transform 0.15s;
  &:hover {
    border-color: #7040e0;
    background: linear-gradient(180deg, rgba(112, 64, 224, 0.07), transparent);
  }
  // Strong visual when files are being dragged over — solid border + tinted
  // bg so the user knows the drop will land here.
  &.is-dragover {
    border-style: solid;
    border-color: #7040e0;
    background: linear-gradient(180deg, rgba(112, 64, 224, 0.14), rgba(112, 64, 224, 0.05));
    transform: scale(1.005);
  }
}
.fp-up-dropicon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  border-radius: 50%;
  background: rgba(112, 64, 224, 0.12);
}
.fp-up-droptitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 700;
  color: #1a0a6b; margin-top: 8px;
}
.fp-up-filesnote {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #5b2ecf;
  margin-top: 4px; font-weight: 600;
}
.fp-up-typebar {
  display: flex; gap: 8px; margin-top: 14px;
  justify-content: center;
}
.fp-up-typebtn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 13px;
  border-radius: 9px;
  border: 1.5px solid rgba(91, 46, 207, 0.2);
  background: #fff; color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700;
  cursor: pointer;
  &.selected {
    background: linear-gradient(135deg, #5b2ecf, #7040e0);
    color: #fff; border-color: transparent;
  }
}
// ===== File thumbnail grid (replaces the old text chips) ===================
// Square tiles, 80px each, auto-fit so they wrap nicely in the dropzone.
// Each tile shows: preview image (or video icon), filename strip at the
// bottom, and an × button in the top-right that fades in on hover.
.fp-up-thumbgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  margin-top: 14px;
}
.fp-up-thumb {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 9px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  cursor: default;
  transition: border-color 0.12s, transform 0.1s;
  img {
    width: 100%; height: 100%; object-fit: cover;
    display: block;
  }
  &:hover {
    border-color: rgba(112, 64, 224, 0.5);
    .fp-up-thumb-x { opacity: 1; }
  }
  // "Add more" variant: dashed border, centered + label.
  &--add {
    border-style: dashed;
    background: rgba(112, 64, 224, 0.04);
    cursor: pointer;
    flex-direction: column;
    gap: 3px;
    padding: 0;
    &:hover { background: rgba(112, 64, 224, 0.08); transform: scale(1.02); }
  }
}
.fp-up-thumb-name {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  padding: 3px 6px;
  background: linear-gradient(180deg, rgba(20, 10, 60, 0) 0%, rgba(20, 10, 60, 0.7) 100%);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  line-height: 1.4;
}
.fp-up-thumb-x {
  position: absolute;
  top: 4px; right: 4px;
  width: 20px; height: 20px;
  border-radius: 50%;
  border: none; padding: 0;
  background: rgba(20, 10, 60, 0.7);
  color: #fff;
  font-size: 14px; line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s;
  &:hover { background: #5b2ecf; }
}
.fp-up-thumb-plus {
  font-family: 'Inter', sans-serif;
  font-size: 28px; font-weight: 300;
  color: #7040e0;
  line-height: 1;
}
.fp-up-thumb-addlabel {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 600;
  color: #5b2ecf;
}

// ===== Price + Payout =====
.fp-up-pricefield {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-radius: 9px;
  background: #fff;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  span { color: #7c6fcd; font-family: 'Inter', sans-serif; font-weight: 600; font-size: 15px; }
  input {
    flex: 1; border: none; outline: none; background: transparent;
    font-family: 'Inter', sans-serif;
    font-size: 14px; color: #1a0a6b; font-weight: 600;
  }
}
.fp-up-pricehint {
  margin-top: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd;
}
.fp-up-payoutcard {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px;
  span:first-child {
    font-family: 'Inter', sans-serif;
    font-size: 11.5px; font-weight: 500; color: #7c6fcd;
  }
}
.fp-up-payout-na {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700; color: #9b90e0;
}
.fp-up-payout-val {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700; color: #1a0a6b;
}

// ===== Compliance toggles =====
.fp-up-flag {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 10px; padding: 10px 0;
  border-bottom: 1px solid rgba(91, 46, 207, 0.08);
  &:last-child { border-bottom: none; padding-bottom: 0; }
  &.is-disabled { opacity: 0.5; }
}
.fp-up-flag-title {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700;
  color: #1a0a6b;
}
.fp-up-flag-desc {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd;
  margin-top: 1px; line-height: 1.35;
}
// Consent-required notice: appears under the toggle when third-party consent
// is on. Pulls the user to fangate.info to manage actual co-creator tags
// (we can't do it via API key).
// ===== Co-creator tagging block (replaces the static "Manage on Fangate"
//       notice now that the consent endpoints are API-accessible) ==========
.fp-up-consent-block {
  margin: 10px 0;
  padding: 12px 14px;
  border-radius: 9px;
  border: 1.5px dashed rgba(16, 185, 129, 0.45);
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.06), transparent);
  display: flex; flex-direction: column; gap: 10px;
}
.fp-up-cotag-quick { display: flex; flex-direction: column; gap: 4px; margin-bottom: 6px; }
.fp-up-cotag-quick-label { font-size: 10.5px; color: var(--ink-muted); }
.fp-up-cotag-chip--pick {
  border: 1px dashed var(--accent-strong, #0f766e); background: transparent;
  color: var(--accent-strong, #0f766e); cursor: pointer;
  &:hover { background: var(--accent-tint, rgba(13, 148, 136, 0.12)); }
}
.fp-up-cotag-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.fp-up-cotag-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 4px 4px 10px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.10);
  border: 1px solid rgba(16, 185, 129, 0.32);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
}
.fp-up-cotag-x {
  width: 18px; height: 18px;
  border-radius: 50%; border: none; padding: 0;
  background: rgba(16, 185, 129, 0.18);
  color: #047857;
  font-size: 13px; line-height: 1; cursor: pointer;
  &:hover { background: #047857; color: #fff; }
}
.fp-up-cotag-input {
  width: 100%; box-sizing: border-box;
  padding: 9px 12px;
  border-radius: 9px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b;
  outline: none;
  &:focus { border-color: #7040e0; box-shadow: 0 0 0 3px rgba(112, 64, 224, 0.12); }
}
.fp-up-cotag-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd;
  font-style: italic;
}
.fp-up-cotag-err {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #b91c1c;
  padding: 6px 10px; border-radius: 7px;
  background: rgba(220, 38, 38, 0.08);
}
.fp-up-cotag-empty {
  display: flex; flex-direction: column; gap: 8px;
}
.fp-up-cotag-results {
  background: #fff;
  border: 1px solid rgba(91, 46, 207, 0.12);
  border-radius: 8px;
  padding: 4px;
  max-height: 200px; overflow-y: auto;
}
.fp-up-cotag-result {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px; border-radius: 6px;
  &:hover { background: rgba(91, 46, 207, 0.04); }
}
.fp-up-cotag-result-meta { flex: 1; min-width: 0; }
.fp-up-cotag-result-name {
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700; color: #1a0a6b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fp-up-cotag-result-email {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: #7c6fcd;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fp-up-cotag-addbtn,
.fp-up-cotag-invitebtn {
  padding: 5px 11px;
  border-radius: 7px;
  border: none;
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.1s, color 0.1s;
  &:hover { background: #047857; color: #fff; }
}
.fp-up-cotag-invitebtn {
  align-self: flex-start;
  padding: 8px 14px;
  border: 1.5px solid rgba(16, 185, 129, 0.4);
  background: rgba(16, 185, 129, 0.06);
}

.fp-up-consent-notice {
  margin: 10px 0;
  padding: 10px 12px;
  border-radius: 9px;
  border: 1.5px dashed rgba(245, 158, 11, 0.45);
  background: linear-gradient(180deg, rgba(245, 158, 11, 0.07), transparent);
}
.fp-up-consent-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 800;
  color: #92400e;
  margin-bottom: 3px;
}
.fp-up-consent-body {
  font-family: 'Inter', sans-serif;
  font-size: 11px; line-height: 1.45;
  color: #78350f;
  a {
    color: #b45309;
    font-weight: 700;
    text-decoration: underline;
  }
}
.fp-up-consent-emph {
  font-weight: 800;
  color: #047857;   // corporate green, matches FangateVaultView
}

// ===== Toggle switch primitive =====
.fp-switch {
  flex-shrink: 0;
  position: relative;
  width: 36px; height: 20px;
  border-radius: 999px;
  border: none; padding: 0;
  background: rgba(91, 46, 207, 0.18);
  cursor: pointer;
  transition: background 0.12s;
  &.on { background: linear-gradient(135deg, #5b2ecf, #7040e0); }
  &.disabled { cursor: not-allowed; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
}
.fp-switch-knob {
  position: absolute; top: 2px; left: 2px;
  width: 16px; height: 16px; border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(11, 20, 20, 0.2);
  transition: transform 0.15s;
  .fp-switch.on & { transform: translateX(16px); }
}

// ===== CTA =====
.fp-up-cta {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px;
  padding: 12px 22px;
  border-radius: 11px; border: none;
  background: linear-gradient(135deg, #1a0a6b, #5b2ecf, #7040e0);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(91, 46, 207, 0.32);
  transition: box-shadow 0.15s, transform 0.1s;
  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(91, 46, 207, 0.42);
  }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
}
</style>
